/**
 * Provider-agnostic ad seam.
 *
 * On a real device this serves AdMob (see `ads-native.ts` / `ads-config.ts`).
 * On the web — and on any native failure — it falls back to a fully-playable
 * MOCK (the `Ad` overlay scene) so lives/hints/interstitials always work, even
 * in the browser where the AdMob plugin is a no-op.
 */
import Phaser from 'phaser';
import { Capacitor } from '@capacitor/core';
import { Progress } from './progress';
import { USE_NATIVE_ADS } from './ads-config';

export type AdKind = 'rewarded' | 'interstitial';

/** Native AdMob is only available inside the Capacitor app shell. */
function nativeReady(): boolean {
  return USE_NATIVE_ADS && Capacitor.isNativePlatform();
}

// The mock overlay lives in its own scene; this bridges its result back to the
// promise returned by `rewarded` / `interstitial`.
let pending: ((completed: boolean) => void) | null = null;

/** Internal: called by the Ad overlay scene when it closes. */
export function _resolveAd(completed: boolean) {
  const resolve = pending;
  pending = null;
  resolve?.(completed);
}

function showMock(scene: Phaser.Scene, kind: AdKind): Promise<boolean> {
  return new Promise((resolve) => {
    if (pending) { const p = pending; pending = null; p(false); }
    pending = resolve;
    scene.scene.launch('Ad', { kind });
    scene.scene.bringToTop('Ad');
  });
}

export const Ads = {
  /**
   * Rewarded ad. Resolves true only if the user watched to completion (reward
   * earned). Callers record their own stat context (hint vs life).
   */
  async rewarded(scene: Phaser.Scene): Promise<boolean> {
    if (nativeReady()) {
      try {
        const { showNativeRewarded } = await import('./ads-native');
        const ok = await showNativeRewarded();
        if (ok) Progress.recordAd();
        return ok;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[ads] native rewarded failed — falling back to mock', e);
      }
    }
    return showMock(scene, 'rewarded'); // the Ad scene records the watch
  },

  /** Interstitial. Resolves when dismissed; updates the cadence clock. */
  async interstitial(scene: Phaser.Scene): Promise<void> {
    if (nativeReady()) {
      try {
        const { showNativeInterstitial } = await import('./ads-native');
        await showNativeInterstitial();
        Progress.recordAd();
        Progress.noteInterstitialShown();
        return;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[ads] native interstitial failed — falling back to mock', e);
      }
    }
    await showMock(scene, 'interstitial'); // the Ad scene records the watch
    Progress.noteInterstitialShown();
  },
};
